import { useCallback, useEffect, useRef, useState } from 'react';

import type { RecentLocation } from '../components/LocationPicker/types';
import { locationService } from '../services/location.service';

interface UseLocationSearchResult {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: RecentLocation[];
  /**
   * The query `searchResults` were fetched for. Compare against the current
   * input before trusting the results — during the debounce window or an
   * in-flight fetch they still belong to an older keystroke.
   */
  searchResultsQuery: string;
  isSearching: boolean;
  recentLocations: RecentLocation[];
  isLoadingCurrentLocation: boolean;
  getCurrentLocation: () => Promise<RecentLocation | undefined>;
  saveRecentLocation: (location: RecentLocation) => void;
}

export function useLocationSearch(): UseLocationSearchResult {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<RecentLocation[]>([]);
  const [searchResultsQuery, setSearchResultsQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [recentLocations, setRecentLocations] = useState<RecentLocation[]>([]);
  const [isLoadingCurrentLocation, setIsLoadingCurrentLocation] = useState(false);
  // Guards against a slow response landing after a newer one.
  const requestIdRef = useRef(0);

  // Load recent locations on mount
  useEffect(() => {
    setRecentLocations(locationService.getRecentLocations());
  }, []);

  // Debounced search effect
  useEffect(() => {
    const searchTimeoutId = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        const requestId = ++requestIdRef.current;
        setIsSearching(true);

        try {
          const results = await locationService.searchLocations(searchQuery);
          if (requestId !== requestIdRef.current) return;
          setSearchResults(results);
          setSearchResultsQuery(searchQuery);
        } catch (error) {
          console.error('[useLocationSearch] Search error:', error);
          if (requestId === requestIdRef.current) {
            setSearchResults([]);
            setSearchResultsQuery(searchQuery);
          }
        } finally {
          if (requestId === requestIdRef.current) {
            setIsSearching(false);
          }
        }
      } else {
        setSearchResults([]);
        setSearchResultsQuery(searchQuery);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(searchTimeoutId);
  }, [searchQuery]);

  const saveRecentLocation = useCallback((location: RecentLocation) => {
    locationService.saveRecentLocation(location);
    setRecentLocations(locationService.getRecentLocations());
  }, []);

  const getCurrentLocation = useCallback(async () => {
    setIsLoadingCurrentLocation(true);
    try {
      const location = await locationService.getCurrentLocation();
      if (location) {
        // Save as recent location
        const recentLocation: RecentLocation = {
          id: location.id,
          name: location.name,
          description: location.description,
          latitude: location.latitude,
          longitude: location.longitude
        };
        saveRecentLocation(recentLocation);
        return recentLocation;
      }
    } catch (error) {
      console.error('Error getting current location:', error);
    } finally {
      setIsLoadingCurrentLocation(false);
    }
  }, [saveRecentLocation]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    searchResultsQuery,
    isSearching,
    recentLocations,
    isLoadingCurrentLocation,
    getCurrentLocation,
    saveRecentLocation
  };
}
